"use client"

import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Edit2, Bell, Calendar, Clock, CheckCircle, User, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import FormModal from '@/components/FormModal';

interface ReminderDetailsProps {
  publicId: string;
}

export default function Reminder({ publicId }: ReminderDetailsProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [reminder, setReminder] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchReminder = async () => {
      setIsLoading(true);
      try {
        // Replace with actual API call when implemented
        // const response = await ReminderService.getReminder(publicId);
        // Mocking data for now
        const mockReminder = {
          title: "Follow up with Client",
          description: "Send the updated proposal to Acme Corp",
          status: "Pending",
          priority: "High",
          dueDate: "2023-12-01",
          dueTime: "14:30",
          assignedTo: {
            name: "John Doe",
            id: "user-123"
          },
          createdAt: "2023-11-15",
          updatedAt: "2023-11-15",
          relatedTo: {
            type: "Deal",
            name: "Acme Corp - Annual Contract",
            id: "deal-456"
          }
        };
        
        setTimeout(() => {
          setReminder(mockReminder);
          setIsLoading(false);
        }, 500);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load reminder details",
          variant: "destructive",
        });
        setIsLoading(false);
      }
    };
    
    if (publicId) {
      fetchReminder();
    }
  }, [publicId, toast]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading reminder details...</p>
      </div>
    );
  }

  if (!reminder) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <p className="text-muted-foreground">Reminder not found</p>
        <Button onClick={() => window.history.back()} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  // Determine status color
  const getStatusBadge = () => {
    switch (reminder.status) {
      case 'Completed':
        return <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">Completed</Badge>;
      case 'Overdue':
        return <Badge variant="outline" className="ml-2 bg-red-50 text-red-700 border-red-200">Overdue</Badge>;
      default:
        return <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">{reminder.status}</Badge>;
    }
  };

  // Determine priority indicator
  const getPriorityBadge = () => {
    switch (reminder.priority) {
      case 'High':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">High</Badge>;
      case 'Medium':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Medium</Badge>;
      case 'Low':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Low</Badge>;
      default:
        return <Badge variant="outline">{reminder.priority}</Badge>;
    }
  };

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">
              {reminder.title}
            </h1>
            {getStatusBadge()}
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline"
              className="gap-2 border-green-200 text-green-700 hover:bg-green-50"
              onClick={() => {
                // Mark as complete functionality
                toast({
                  title: "Success",
                  description: "Reminder marked as complete",
                });
              }}
            >
              <CheckCircle className="w-4 h-4" />
              Mark Complete
            </Button>
            <Button 
              onClick={() => setModalOpen(true)} 
              variant="outline"
              className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Reminder Overview */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Reminder Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Due Date</p>
                    <p className="text-sm text-muted-foreground">{reminder.dueDate}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Due Time</p>
                    <p className="text-sm text-muted-foreground">{reminder.dueTime}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Priority</p>
                    <div className="mt-1">{getPriorityBadge()}</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Assigned To</p>
                    <p className="text-sm text-muted-foreground">{reminder.assignedTo.name}</p>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Related To */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Related To</p>
                <div className="p-3 border rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-muted-foreground">{reminder.relatedTo.type}</p>
                      <p className="font-medium">{reminder.relatedTo.name}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <ArrowLeft className="w-4 h-4 rotate-[225deg]" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Description */}
          <Card className="md:col-span-2 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-base">{reminder.description}</p>
              
              <div className="mt-8 pt-6 border-t space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Created</span>
                  <span>{reminder.createdAt}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span>{reminder.updatedAt}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Edit Modal - To be implemented */}
      {isModalOpen && (
        <FormModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          title="Edit Reminder"
        >
          <div className="p-4">
            <p>Reminder edit form will be implemented here.</p>
          </div>
        </FormModal>
      )}
    </div>
  );
} 